import moment from 'moment';
import { WizardPart } from './wizard-part';
import { ProgressReporter } from './progress-reporter';
import { Child, Country } from '@/client/models';
import { StepData as YourDataStepData } from './your-data-step-provider';
import LogRocket from 'logrocket';
import { NetworkUtils } from '@/networking/network-utils';

export interface StepData {
    child?: Child;
    startAt?: moment.Moment;
    country?: string;
    zipCode?: string;
    number?: string;
    street?: string;
    city?: string;
}

export class WelcomeStepProvider {
    public data: StepData;
    private reporter: ProgressReporter;
    private countries: Country[];
    private contractStart: string;

    constructor(data: StepData, reporter: ProgressReporter) {
        this.data = data;

        this.reporter = reporter;
        this.initialize = this.initialize.bind(this);
        this.getCountries = this.getCountries.bind(this);
        this.getSelectedCountry = this.getSelectedCountry.bind(this);
        this.getFormData = this.getFormData.bind(this);
        this.setFormData = this.setFormData.bind(this);
        this.isFirstNameRequired = this.isFirstNameRequired.bind(this);
        this.setDateOfBirth = this.setDateOfBirth.bind(this);
        this.setStartAt = this.setStartAt.bind(this);
        this.setCountry = this.setCountry.bind(this);
        this.next = this.next.bind(this);
    }

    public initialize(countries: Country[], contractStart: string): void {
        this.countries = countries;
        if (this.data.child == null) {
            this.data.child = { gender: undefined, dateOfBirth: undefined };
        }
        this.contractStart = contractStart;

        this.changed();
    }

    public getStartAt(): moment.Moment {
        return this.data.startAt;
    }

    public getContractStart(): string {
        return this.contractStart;
    }

    public getCountries(): Country[] {
        return this.countries;
    }

    public getSelectedCountry(): Country {
        return this.countries?.find(x => x.countryId === this.data.country);
    }

    public getSelectedZipCode(): string {
        return this.data.zipCode;
    }

    public getChosenNumber(): string {
        return this.data.number;
    }

    public getFormData(): StepData {
        return { ...this.data };
    }

    public setFormData(data: StepData): void {
        if (data.country != null) {
            this.data.country = data.country;
        }

        this.data.child.dateOfBirth = data.child.dateOfBirth?.startOf('day');
        this.data.child.firstName = data.child.firstName;
        this.data.startAt = data.startAt?.startOf('day');
        this.data.zipCode = data.zipCode;
        this.data.street = data.street;
        this.data.city = data.city;
        this.data.number = data.number;
        this.changed();
    }

    public setCountry(country: string): void {
        this.data.country = country;
        this.changed();
    }

    public setSelectedZipCode(zipCode: string): void {
        this.data.zipCode = zipCode;
        this.reporter.partIncomplete(WizardPart.welcome);
        this.changed();
    }

    public resetAddressData(): void {
        this.data.zipCode = '';
        this.data.street = '';
        this.data.number = '';
        this.data.city = '';

        this.reporter.partIncomplete(WizardPart.welcome);
        this.changed();
    }

  

    public setDateOfBirth(date: moment.Moment): void {
        this.data.child.dateOfBirth = date?.startOf('day');
        this.changed();
    }

    public setStartAt(date: moment.Moment): void {
        this.data.startAt = date?.startOf('day');
        this.changed();
    }

    public isFirstNameRequired(): boolean {
        if (this.data.child.dateOfBirth == null) {
            return false;
        }

        return this.data.child.dateOfBirth.isBefore(moment.now());
    }

    public setAddress(city: string, street: string, number: string, zipCode?: string): void {
        const addressData = this.data as YourDataStepData;

        if (zipCode) {
            addressData.zipCode = zipCode;
        }

        if (addressData.city === city && addressData.street === street && addressData.number === number) {
            return;
        }

        addressData.city = city;
        addressData.street = street;
        addressData.number = number;
        this.changed();
    }

    public next(): void {

        LogRocket.identify(`${this.data.child.firstName}:${this.data.zipCode}:${this.data.child.dateOfBirth.format('yyyy-MM-DD')}`, {
            child: this.data.child.firstName,
            dateOfBirth: this.data.child.dateOfBirth.format('yyyy-MM-DD'),
            postalCode: this.data.zipCode,
            tenant: NetworkUtils.getTenantFromUrl()
        });

        this.reporter.partChanged(WizardPart.welcome);
    }

    private changed(): void {
        this.reporter.partChanged(WizardPart.welcome, false);
    }
}
